import { createSlice } from '@reduxjs/toolkit';

import type { ProcessoArbitralRascunhoState } from '@/features/processo-arbitral/types/rascunhoStepTypes';

import {
  discardProcessoArbitralDraft,
  fetchProcessoArbitralRascunhos,
  initializeProcessoArbitralDraft,
  saveProcessoArbitralDraftSnapshot,
} from './actions';

const initialState: ProcessoArbitralRascunhoState = {
  activeDraft: null,
  items: [],
  total: 0,
  totalGeral: 0,
  page: 1,
  totalPages: 1,
  escritorioId: null,
  isInitializing: false,
  isLoadingList: false,
  isSaving: false,
  isDiscarding: false,
  error: null,
  listError: null,
};

const rascunhoStepSlice = createSlice({
  name: 'processoArbitralRascunhoStep',
  initialState,
  reducers: {
    resetProcessoArbitralRascunhoState: () => initialState,
    clearActiveProcessoArbitralDraft: (state) => {
      state.activeDraft = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProcessoArbitralRascunhos.pending, (state, action) => {
        const escritorioId = action.meta.arg?.escritorioId ?? null;

        if (escritorioId !== state.escritorioId) {
          state.escritorioId = escritorioId;
          state.items = [];
          state.total = 0;
          state.totalGeral = 0;
          state.page = 1;
          state.totalPages = 1;
        }

        state.isLoadingList = true;
        state.listError = null;
      })
      .addCase(fetchProcessoArbitralRascunhos.fulfilled, (state, action) => {
        if (action.payload.escritorioId !== state.escritorioId) {
          return;
        }

        state.isLoadingList = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        if (!action.meta.arg?.search) {
          state.totalGeral = action.payload.total;
        }
      })
      .addCase(fetchProcessoArbitralRascunhos.rejected, (state, action) => {
        if ((action.meta.arg?.escritorioId ?? null) !== state.escritorioId) {
          return;
        }

        state.isLoadingList = false;
        state.listError = action.payload || 'Erro ao listar rascunhos arbitrais';
      })
      .addCase(initializeProcessoArbitralDraft.pending, (state) => {
        state.isInitializing = true;
        state.error = null;
      })
      .addCase(initializeProcessoArbitralDraft.fulfilled, (state, action) => {
        state.isInitializing = false;
        state.activeDraft = action.payload;
      })
      .addCase(initializeProcessoArbitralDraft.rejected, (state, action) => {
        state.isInitializing = false;
        state.error = action.payload || 'Erro ao inicializar rascunho arbitral';
      })
      .addCase(saveProcessoArbitralDraftSnapshot.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveProcessoArbitralDraftSnapshot.fulfilled, (state, action) => {
        const { draft, criado } = action.payload;

        state.isSaving = false;
        state.activeDraft = draft;
        state.items = [draft, ...state.items.filter((item) => item.id !== draft.id)];

        if (criado) {
          state.total += 1;
          state.totalGeral += 1;
        }
      })
      .addCase(saveProcessoArbitralDraftSnapshot.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload || 'Erro ao salvar rascunho arbitral';
      })
      .addCase(discardProcessoArbitralDraft.pending, (state) => {
        state.isDiscarding = true;
        state.error = null;
      })
      .addCase(discardProcessoArbitralDraft.fulfilled, (state, action) => {
        state.isDiscarding = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        state.totalGeral = Math.max(0, state.totalGeral - 1);

        if (state.activeDraft?.id === action.payload) {
          state.activeDraft = null;
        }
      })
      .addCase(discardProcessoArbitralDraft.rejected, (state, action) => {
        state.isDiscarding = false;
        state.error = action.payload || 'Erro ao descartar rascunho arbitral';
      });
  },
});

export const { clearActiveProcessoArbitralDraft } = rascunhoStepSlice.actions;

export const rascunhoStepReducer = rascunhoStepSlice.reducer;
